import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import SummaryCards from "../SummaryCards";
import DataTable from "../DataTable";
import {
  formatDateTime,
  getFieldValue,
  getPrimaryArray,
} from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const DORMANT_USER_COLUMNS = [
  {
    key: "email",
    label: "Email",
    render: (_, row) => getFieldValue(row, ["email"]),
  },
  {
    key: "last_login_at",
    label: "Last Login At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_login_at", "lastLoginAt"])),
  },
  {
    key: "last_activity_at",
    label: "Last Activity At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_activity_at", "lastActivityAt"])),
  },
  {
    key: "created_at",
    label: "Created At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["created_at", "createdAt"])),
  },
];

const RetentionReportPanel = ({ paidOnly = false }) => {
  const [query, setQuery] = useState({
    dormant_days: 14,
    cohort_days: 90,
  });
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminAnalyticsService.getRetentionReport({
        ...query,
        paid_only: paidOnly,
      });
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load retention report.");
    } finally {
      setLoading(false);
    }
  }, [paidOnly, query]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const dormantUsers = useMemo(
    () => getPrimaryArray(data, ["dormant_users", "users", "rows", "items"]),
    [data]
  );
  const summaryEntries = useMemo(() => {
    const summary = data?.summary || data || {};
    return [
      ["cohort window days", summary.cohort_days],
      ["activated users in cohort", summary.activated_users],
      ["returned within 7 days pct", summary.return_7d_pct],
      ["returned within 14 days pct", summary.return_14d_pct],
      ["returned within 30 days pct", summary.return_30d_pct],
      ["dormant threshold days", summary.dormant_days],
      ["dormant users count", summary.dormant_users_count],
    ].filter(([, value]) => value !== undefined);
  }, [data]);

  const onApply = (event) => {
    event.preventDefault();
    setQuery(draft);
  };

  return (
    <section className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <div>
        <h3 className="text-2xl font-semibold text-slate-900">Retention</h3>
        <p className="mt-1 text-sm text-slate-500">
          Monitor cohort return rates and dormant learners. Return percentages use activated users as the denominator, while dormant users are counted separately using the inactivity threshold.
        </p>
      </div>

      <form
        onSubmit={onApply}
        className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,220px)_minmax(0,220px)_auto] xl:items-end">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Dormant Days
          </span>
          <input
            type="number"
            min={1}
            max={365}
            value={draft.dormant_days}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, dormant_days: Number(e.target.value) }))
            }
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Cohort Days
          </span>
          <input
            type="number"
            min={7}
            max={365}
            value={draft.cohort_days}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, cohort_days: Number(e.target.value) }))
            }
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
          />
        </label>

        <button
          type="submit"
          className="min-w-[160px] rounded-xl bg-[#54B0AF] px-5 py-2.5 text-sm font-medium text-white shadow hover:opacity-90"
          disabled={loading}
        >
          Apply
        </button>
        </div>
      </form>

      {loading && <SectionLoading text="Loading retention report..." />}
      {error && <SectionError message={error} />}

      {!loading && !error && (
        <div className="space-y-4">
          <SummaryCards entries={summaryEntries} />
          <DataTable
            title="Dormant Users"
            subtitle="Learners with no recent login or activity inside the selected dormant window."
            rows={dormantUsers}
            columns={DORMANT_USER_COLUMNS}
            emptyText="No dormant users found for current filters."
          />
        </div>
      )}
    </section>
  );
};

export default RetentionReportPanel;

